import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Music } from "lucide-react";

// Clean hip-hop instrumental (no lyrics = no cussing). Free / no-restriction from Internet Archive.
// Swap this URL anytime — see /app/frontend/src/components/site/MusicPlayer.jsx
// To use your own file: drop an MP3 in /app/frontend/public/audio/ and reference "/audio/yourfile.mp3"
const TRACK_URL = "https://archive.org/download/mindcrack_beatpack_001/090bpm___bazrap-chillermix-01___srsrsrr_64kb.mp3";
const TRACK_TITLE = "Chill Hip-Hop Beat";

const STORAGE_KEY = "tbm_music_pref";

export default function MusicPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [firstInteraction, setFirstInteraction] = useState(false);

  // Restore user preference
  useEffect(() => {
    try {
      const pref = localStorage.getItem(STORAGE_KEY);
      if (pref === "playing") {
        // Browsers block autoplay w/ sound unless user has interacted.
        // We'll show a small badge; play kicks in on first click anywhere.
        setPlaying(true);
      }
    } catch {
      // localStorage unavailable — ignore
    }
  }, []);

  // Attempt play/pause when playing state changes
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.volume = 0.25;
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay blocked → keep UI in "paused" state
          setPlaying(false);
        });
      }
    } else {
      a.pause();
    }
    try {
      localStorage.setItem(STORAGE_KEY, playing ? "playing" : "paused");
    } catch {
      // localStorage unavailable — ignore
    }
  }, [playing]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  const toggle = () => {
    setFirstInteraction(true);
    setPlaying((p) => !p);
  };

  return (
    <>
      <audio
        ref={audioRef}
        src={TRACK_URL}
        loop
        preload="none"
        onCanPlay={() => setLoaded(true)}
        onError={() => setLoaded(false)}
      />
      <div
        className="fixed z-40 bottom-20 left-4 lg:bottom-6 lg:left-6"
        data-testid="music-player"
      >
        <div
          className={`bg-[#151816] text-white border border-[#5BB239]/40 shadow-lg transition-all duration-300 overflow-hidden ${
            expanded ? "w-[280px]" : "w-14"
          }`}
        >
          {expanded ? (
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${playing ? "bg-[#5BB239]" : "bg-white/10"}`}>
                    <Music className={`w-4 h-4 ${playing ? "text-white animate-pulse" : "text-white/60"}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono-label text-[9px] text-[#5BB239] tracking-widest">
                      {playing ? "Now Playing" : "Vibe"}
                    </div>
                    <div className="text-xs font-bold truncate">{TRACK_TITLE}</div>
                  </div>
                </div>
                <button
                  onClick={() => setExpanded(false)}
                  className="text-white/50 hover:text-white text-xs font-mono-label px-2"
                  data-testid="music-collapse"
                  aria-label="Collapse"
                >
                  ×
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={toggle}
                  className="flex-1 flex items-center justify-center gap-2 bg-[#5BB239] hover:bg-[#499828] text-white py-2.5 font-mono-label text-[10px] uppercase tracking-widest transition-colors"
                  data-testid="music-toggle"
                >
                  {playing ? (
                    <>
                      <Pause className="w-3.5 h-3.5" /> Pause
                    </>
                  ) : (
                    <>
                      <Play className="w-3.5 h-3.5" /> Play
                    </>
                  )}
                </button>
                <button
                  onClick={() => setMuted((m) => !m)}
                  className="w-10 h-[38px] flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                  data-testid="music-mute"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
              </div>
              {!loaded && firstInteraction && (
                <div className="mt-2 text-[10px] text-white/50">Loading track…</div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setExpanded(true)}
              className="w-14 h-14 flex items-center justify-center hover:bg-[#1e2320] transition-colors group"
              aria-label="Open music player"
              data-testid="music-open"
            >
              <div className="relative">
                <Music className={`w-5 h-5 ${playing ? "text-[#5BB239]" : "text-white/70"} transition-colors`} />
                {playing && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#5BB239] animate-pulse" />
                )}
              </div>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
